/**
 * Full-screen client picker for the Manager app. Sets (or clears) the active
 * client. Clearing returns the app to firm-wide mode ("All clients").
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Check, Users, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';
import { useActiveClient } from './active-client';

export default function ClientPicker({ onClose }: { onClose: () => void }) {
  const { activeClient, setActiveClient } = useActiveClient();
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['ma-client-list'],
    queryFn: async () => { const r = await apiClient.get('/firm/dashboard'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const clients = useMemo(() => {
    const raw = data?.clients || [];
    return raw
      .map((c: any) => ({ id: c.id, name: c.display_name }))
      .filter((c: any) => c.id && c.name)
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? clients.filter((c: any) => c.name.toLowerCase().includes(q)) : clients;
  }, [clients, search]);

  const pick = (c: { id: string; name: string } | null) => { setActiveClient(c); onClose(); };

  return (
    <div className="fixed inset-0 z-[65] flex flex-col bg-background animate-in fade-in duration-150">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 border-b border-border bg-card/95 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+1rem)]">
        <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-base font-semibold text-foreground">Focus a client</h2>
      </div>

      {/* Search */}
      <div className="shrink-0 px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            type="text"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-border bg-muted/40 py-2.5 pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-[var(--v4-accent)]/40 focus:outline-none"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        {/* Firm-wide (clear) option */}
        <button
          onClick={() => pick(null)}
          className={cn(
            'mb-2 flex w-full items-center gap-3 rounded-xl border p-3 text-left transition',
            !activeClient ? 'border-[var(--v4-accent)]/40 bg-[var(--v4-accent)]/5' : 'border-border bg-card hover:bg-muted/50'
          )}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Users className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">All clients</p>
            <p className="text-xs text-muted-foreground">Firm-wide - chat and timeline cover the whole firm</p>
          </div>
          {!activeClient && <Check className="h-4 w-4 text-[var(--v4-accent)]" />}
        </button>

        {isLoading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--v4-accent)]" /></div>
        ) : filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{search ? 'No clients match your search' : 'No clients found'}</p>
        ) : (
          <div className="space-y-1.5">
            {filtered.map((c: any) => {
              const isActive = activeClient?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => pick(c)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition',
                    isActive ? 'border-[var(--v4-accent)]/40 bg-[var(--v4-accent)]/5' : 'border-border bg-card hover:bg-muted/50'
                  )}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--v4-accent)]/15 text-sm font-semibold text-[var(--v4-accent)]">
                    {c.name.slice(0, 1).toUpperCase()}
                  </div>
                  <p className="flex-1 min-w-0 truncate text-sm font-medium text-foreground">{c.name}</p>
                  {isActive && <Check className="h-4 w-4 shrink-0 text-[var(--v4-accent)]" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
